import { EntityType } from '@frontend/common';
import { ICONS, Package } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { PackageType } from '@frontend/package/types';
import { LuCheck, LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { PackageProofOfIdentityRequirement } from '../../utils/resolve-package-proof-of-identity-requirements';
import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import useConfirmTransaction from './confirm-transaction.controller';

const ID = 'confirm-transaction';

const ProofOfIdentityRequirementLines = ({ requirements }: { requirements: PackageProofOfIdentityRequirement[] }) => {
    if (requirements.length === 0) return null;

    return (
        <div className='flex flex-col gap-1'>
            {requirements.map((requirement) => (
                <div
                    key={requirement.packageType}
                    className='text-brand-muted flex flex-wrap items-center gap-1 text-sm font-medium'>
                    {requirement.packageType === PackageType.DROP_OFF ? CommonMessage.BUTTONS.DROP_OFF : CommonMessage.BUTTONS.PICK_UP}
                    <span>:</span>
                    <FormattedMessage
                        id='platform.wizard.confirm_transaction.proof_of_identity_required'
                        description='Label indicating proof of identity is required on the confirm transaction screen'
                        defaultMessage='Proof of identity required'
                    />
                </div>
            ))}
        </div>
    );
};

const ConfirmTransaction = (props: { wizard_id: string }) => {
    const viewProps = useConfirmTransaction(props);

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: viewProps.submitDisabled,
        continueLabel: (
            <>
                {CommonMessage.BUTTONS.CONFIRM} {CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
            </>
        )
    });

    return (
        <div
            id={ID}
            className='flex w-full flex-col gap-3'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>
                {CommonMessage.BUTTONS.CONFIRM} {CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
            </div>
            <div className='flex w-full flex-col items-start justify-start gap-3'>
                <div className='text-foreground-default justify-start text-base font-medium'>
                    {CommonMessage.DETAIL.DETAILS_OF(CommonMessage.OBJECTS.TRANSACTION.DEFAULT)}
                </div>
                <div className='flex w-full flex-col items-start justify-start gap-2'>
                    {viewProps.transaction && (
                        <div className='outline-border-default inline-flex w-full flex-col items-start justify-start gap-2 self-stretch rounded-xl p-4 outline-1'>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOT}</div>
                                <PlatformObjectBadge
                                    className='text-foreground-default flex items-center justify-start gap-1 text-base font-medium'
                                    entityType={EntityType.SPOT}
                                    id={viewProps.transaction.spot_id || undefined}
                                    disableClick
                                    key={viewProps.transaction.spot_id || undefined}
                                />
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.tracking_number}</div>
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.purchase_order}</div>
                            </div>
                            <div className='inline-flex h-6 items-center justify-between self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.SUPPLIER.DEFAULT}</div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{viewProps.transaction.supplier ?? '/'}</div>
                            </div>
                        </div>
                    )}

                    {(viewProps.collis?.length ?? 0) > 0 && (
                        <div className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                            <div className='inline-flex items-center justify-between self-stretch'>
                                <div className='flex items-center justify-start gap-4'>
                                    <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                        <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                        <Package className='size-1/2' />
                                    </div>

                                    <div className='inline-flex flex-col items-start justify-center gap-1'>
                                        <div className='text-foreground-default justify-start text-base font-semibold'>
                                            {CommonMessage.OBJECTS.PARCEL.PLURAL} {`(${viewProps.collis?.[0].type})`}
                                        </div>
                                        <div className='text-brand-muted justify-start text-base font-medium'>
                                            {viewProps.collis?.length ?? 0} {CommonMessage.OBJECTS.TRANSACTION.ITEMS}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                        <div className='inline-flex items-center justify-between self-stretch'>
                            <div className='flex items-center justify-start gap-4'>
                                <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                    {viewProps.senders && viewProps.senders.length > 0 ? (
                                        <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    ) : (
                                        <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    )}
                                    <ICONS.ENTITY.USER className='size-1/2' />
                                </div>

                                <div className='inline-flex flex-col items-start justify-center gap-1'>
                                    <div className='text-foreground-default justify-start text-base font-semibold'>
                                        {CommonMessage.OBJECTS.TRANSACTION.SENDER}
                                    </div>
                                    {viewProps.senders?.map((sender) => (
                                        <PlatformObjectBadge
                                            key={sender.user_id}
                                            id={sender.user_id}
                                            entityType={EntityType.USER}
                                            className='text-brand-muted flex items-center justify-start gap-1 text-base font-medium'
                                            disableClick
                                        />
                                    ))}
                                    <ProofOfIdentityRequirementLines requirements={viewProps.senderProofRequirements} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='outline-border-default inline-flex flex-col items-start justify-start gap-5 self-stretch rounded-xl p-4 outline-1'>
                        <div className='inline-flex items-center justify-between self-stretch'>
                            <div className='flex items-center justify-start gap-4'>
                                <div className='outline-border-default relative flex size-12 items-center justify-center rounded-full outline-2'>
                                    {viewProps.receivers && viewProps.receivers.length > 0 ? (
                                        <LuCheck className='bg-status-success text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    ) : (
                                        <LuX className='bg-brand-muted text-background-surface absolute top-0 right-0 size-4 rounded-full p-0.5' />
                                    )}
                                    <ICONS.ENTITY.USER className='size-1/2' />
                                </div>

                                <div className='inline-flex flex-col items-start justify-center gap-1'>
                                    <div className='text-foreground-default justify-start text-base font-semibold'>
                                        {CommonMessage.OBJECTS.TRANSACTION.RECEIVER}
                                    </div>
                                    {viewProps.receivers?.map((receiver) => (
                                        <PlatformObjectBadge
                                            key={receiver.user_id}
                                            id={receiver.user_id}
                                            entityType={EntityType.USER}
                                            className='text-brand-muted flex items-center justify-start gap-1 text-base font-medium'
                                            disableClick
                                        />
                                    ))}
                                    <ProofOfIdentityRequirementLines requirements={viewProps.receiverProofRequirements} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmTransaction;
